/**
 * IterationChain Component
 *
 * Story FOS-4.2.4: Leaf Components Batch 2 (Collaboration)
 * Task 1: Copy Collaboration Components
 *
 * Visual timeline showing the iteration chain from original discussion
 * through proposals and continuations.
 *
 * @see AC-4.2.4.5 - IterationChain Component Migrated
 *
 * @module components/governance/IterationChain
 */

import { useState } from 'react';
import {
  MessageSquare,
  FileText,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Clock,
  Edit2,
} from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

/**
 * Chain node type
 */
export type ChainNodeType = 'discussion' | 'proposal' | 'continuation';

/**
 * Chain node status
 */
export type ChainNodeStatus =
  | 'draft'
  | 'in-progress'
  | 'completed'
  | 'submitted'
  | 'rejected'
  | 'passed';

/**
 * Single node in the iteration chain
 */
export interface ChainNode {
  /** Unique identifier */
  id: string;
  /** Node type */
  type: ChainNodeType;
  /** Display title */
  title: string;
  /** Current status */
  status: ChainNodeStatus;
  /** URL to navigate to */
  url: string;
  /** Timestamp for ordering (optional, defaults to 0) */
  timestamp?: number;
}

/** Minimum number of nodes before collapsible behavior activates */
const COLLAPSIBLE_THRESHOLD = 3;

/**
 * Props for IterationChain component
 */
export interface IterationChainProps {
  /** Chain nodes in chronological order */
  nodes: ChainNode[];
  /** Currently active node ID */
  currentNodeId?: string;
  /** Callback when node is clicked */
  onNodeClick?: (node: ChainNode) => void;
  /** Enable collapsible behavior for long chains */
  collapsible?: boolean;
  /** Start collapsed */
  defaultCollapsed?: boolean;
  /** Compact display mode */
  compact?: boolean;
  /** Chain orientation */
  orientation?: 'horizontal' | 'vertical';
  /** Additional CSS classes */
  className?: string;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Get icon for node type
 */
function getNodeIcon(type: ChainNodeType) {
  switch (type) {
    case 'discussion':
      return MessageSquare;
    case 'proposal':
      return FileText;
    case 'continuation':
      return RefreshCw;
  }
}

/**
 * Get status badge color
 */
function getStatusColor(status: ChainNodeStatus): string {
  switch (status) {
    case 'completed':
    case 'passed':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'rejected':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    case 'in-progress':
    case 'submitted':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'draft':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  }
}

/**
 * Get status icon
 */
function getStatusIcon(status: ChainNodeStatus) {
  switch (status) {
    case 'completed':
    case 'passed':
      return Check;
    case 'rejected':
      return X;
    case 'in-progress':
    case 'submitted':
      return Clock;
    case 'draft':
      return Edit2;
  }
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Chain node display
 */
function ChainNodeItem({
  node,
  isCurrent,
  compact,
  onClick,
}: {
  node: ChainNode;
  isCurrent: boolean;
  compact: boolean;
  onClick?: () => void;
}) {
  const Icon = getNodeIcon(node.type);
  const StatusIcon = getStatusIcon(node.status);
  const statusColor = getStatusColor(node.status);

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      data-testid={`chain-node-${node.id}`}
      data-type={node.type}
      className={`
        chain-node flex items-center gap-2 p-2 rounded-lg border border-border
        hover:bg-muted/50 transition-colors
        ${isCurrent ? 'ring-2 ring-primary bg-muted/30' : ''}
        ${compact ? 'compact p-1.5' : ''}
      `}
    >
      <a
        href={node.url}
        onClick={handleClick}
        className="flex items-center gap-2 text-sm no-underline"
      >
        {/* Type Icon */}
        <span className="flex-shrink-0 p-1 rounded bg-muted">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </span>

        {/* Title */}
        <span className="font-medium text-foreground truncate max-w-[150px]">
          {node.title}
        </span>
      </a>

      {/* Status Badge */}
      {!compact && (
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusColor}`}>
          <StatusIcon className="h-3 w-3" />
          {node.status}
        </span>
      )}
    </div>
  );
}

/**
 * Connector between nodes
 */
function ChainConnector({ vertical }: { vertical: boolean }) {
  return (
    <div
      data-testid="chain-connector"
      className={`flex items-center justify-center ${vertical ? 'py-1' : 'px-1'}`}
    >
      {vertical ? (
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      ) : (
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * IterationChain - Visual timeline of discussion → proposal → continuation
 *
 * @example
 * ```tsx
 * <IterationChain
 *   nodes={[
 *     { id: 'disc-1', type: 'discussion', title: 'Original', status: 'completed', url: '/d/1', timestamp: 1000 },
 *     { id: 'prop-1', type: 'proposal', title: 'Proposal #123', status: 'rejected', url: '/p/123', timestamp: 2000 },
 *     { id: 'cont-1', type: 'continuation', title: 'Continuation', status: 'in-progress', url: '/d/2', timestamp: 3000 },
 *   ]}
 *   currentNodeId="cont-1"
 * />
 * ```
 */
export function IterationChain({
  nodes,
  currentNodeId,
  onNodeClick,
  collapsible = false,
  defaultCollapsed = false,
  compact = false,
  orientation = 'horizontal',
  className = '',
}: IterationChainProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // Empty state
  if (nodes.length === 0) {
    return (
      <div
        data-testid="iteration-chain"
        className={`iteration-chain text-sm text-muted-foreground italic ${className}`}
      >
        No iteration history available
      </div>
    );
  }

  // Sort by timestamp (undefined timestamps default to 0)
  const sortedNodes = [...nodes].sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

  // Determine visible nodes when collapsed
  const showCollapsed = collapsible && collapsed && sortedNodes.length > COLLAPSIBLE_THRESHOLD;
  const visibleNodes = showCollapsed
    ? [sortedNodes[0], sortedNodes[sortedNodes.length - 1]]
    : sortedNodes;
  const hiddenCount = sortedNodes.length - visibleNodes.length;

  const isVertical = orientation === 'vertical';

  return (
    <div
      data-testid="iteration-chain"
      className={`
        iteration-chain
        ${isVertical ? 'vertical flex flex-col' : 'horizontal flex flex-row flex-wrap items-center'}
        ${compact ? 'compact gap-1' : 'gap-2'}
        ${className}
      `}
    >
      {visibleNodes.map((node, index) => (
        <div
          key={node.id}
          className={`flex ${isVertical ? 'flex-col' : 'flex-row'} items-center`}
        >
          {/* Connector before (except first) */}
          {index > 0 && <ChainConnector vertical={isVertical} />}

          {/* Show "X more" indicator when collapsed */}
          {showCollapsed && index === 1 && (
            <>
              <ChainConnector vertical={isVertical} />
              <button
                onClick={() => setCollapsed(false)}
                className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border rounded"
                aria-label="Expand chain"
              >
                {hiddenCount} more
              </button>
              <ChainConnector vertical={isVertical} />
            </>
          )}

          {/* Node */}
          <ChainNodeItem
            node={node}
            isCurrent={node.id === currentNodeId}
            compact={compact}
            onClick={onNodeClick ? () => onNodeClick(node) : undefined}
          />
        </div>
      ))}

      {/* Collapse/Expand toggle */}
      {collapsible && sortedNodes.length > COLLAPSIBLE_THRESHOLD && (
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="ml-2 p-1 text-xs text-muted-foreground hover:text-foreground"
          aria-label={collapsed ? 'Expand chain' : 'Collapse chain'}
        >
          {collapsed ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronUp className="h-4 w-4" />
          )}
        </button>
      )}
    </div>
  );
}

export default IterationChain;
